import { feature as asFeature, featureCollection as asFeatureCollection } from '@turf/helpers';
import { default as OlFeature } from 'ol/Feature';

import { SingleTaskWebWorkerApi } from 'cadenza/web-worker/single-task-web-worker-api';
import {
  geoJsonFeatureToOlFeature,
  olGeometryToGeoJsonGeometry
} from 'cadenza/utils/geo-json';
import { uniqueId } from 'cadenza/utils/unique-id';

import type { Polygon as OlPolygon } from 'ol/geom';
import type { Polygon as GeoJsonPolygon, Feature as GeoJsonFeature } from 'geojson';

const POLYGON_MERGE_STRATEGY_TYPE_VALUES = [
  'Union',
  'Difference',
  'Intersection',
  'UnionWithoutIntersection'
] as const;

export type PolygonMergeStrategyType = typeof POLYGON_MERGE_STRATEGY_TYPE_VALUES[number];

const WORKER_BY_TYPE: Record<PolygonMergeStrategyType, () => Worker> = {
  Union: () => new Worker(new URL('./union-polygon-merge-strategy.worker.ts', import.meta.url)),
  Difference: () => new Worker(new URL('./difference-polygon-merge-strategy.worker.ts', import.meta.url)),
  Intersection: () => new Worker(new URL('./intersection-polygon-merge-strategy.worker.ts', import.meta.url)),
  UnionWithoutIntersection: () => new Worker(new URL('./union-without-intersection-polygon-merge-strategy.worker.ts', import.meta.url))
};

export type Mergeable = OlPolygon | OlFeature<OlPolygon> | (OlPolygon | OlFeature<OlPolygon>)[];

export class PolygonMergeStrategy extends SingleTaskWebWorkerApi {

  /**
   * Merges each operator into the base in a one by one fashion.
   *
   * @param base
   * @param operator
   */
  async merge (base: Mergeable, operator: Mergeable): Promise<OlFeature<OlPolygon>[]> {
    const baseArray = Array.isArray(base) ? base : [ base ];
    const operatorArray = Array.isArray(operator) ? operator : [ operator ];

    const geoJsonBase = asFeatureCollection(baseArray.map(toGeoJsonFeatureWithId));
    const geoJsonOperators = asFeatureCollection(operatorArray.map(toGeoJsonFeatureWithId));

    const result = await this.execute([ geoJsonBase, geoJsonOperators ]) as GeoJsonFeature<GeoJsonPolygon>[];
    return result.map(geoJsonFeature => geoJsonFeatureToOlFeature(geoJsonFeature) as OlFeature<OlPolygon>);
  }

}

function toGeoJsonFeatureWithId (mergeable:  OlPolygon | OlFeature<OlPolygon>) {
  if (mergeable instanceof OlFeature) {
    return createGeoJsonFeatureWithId(mergeable.getGeometry()!, mergeable.getId());
  }
  return createGeoJsonFeatureWithId(mergeable);
}

function createGeoJsonFeatureWithId (polygon: OlPolygon, id?: string | number) {
  return asFeature(olGeometryToGeoJsonGeometry(polygon), {}, { id: id ?? uniqueId() });
}

export function spawnPolygonMergeStrategyWorker (type: PolygonMergeStrategyType) {
  const worker = WORKER_BY_TYPE[type]();
  return new PolygonMergeStrategy(worker);
}
